import { useEffect, useMemo, type ReactNode } from 'react';
import { useDraggable } from '../../../hooks/useDraggable';
import { useResizable } from '../../../hooks/useResizable';
import { getCSSVariable } from '../../../util/cssVariables';
import styles from './FloatingLayout.module.css';

interface FloatingWindowProps {
  children: ReactNode;
  position: { x: number; y: number };
  size: { width: number; height: number };
  onPositionChange: (position: { x: number; y: number }) => void;
  onSizeChange: (size: { width: number; height: number }) => void;
  onClose: () => void;
  enableDrag?: boolean;
  enableResize?: boolean;
  navigationLayout?: 'sidebar' | 'horizontal';
}

export function FloatingWindow({
  children,
  position,
  size,
  onPositionChange,
  onSizeChange,
  onClose,
  enableDrag = true,
  enableResize = true,
  navigationLayout = 'sidebar',
}: FloatingWindowProps) {
  const constraints = useMemo(
    () => ({
      minWidth: getCSSVariable('--devxgpt-floating-min-width', 300),
      minHeight: getCSSVariable('--devxgpt-floating-min-height', 350),
      maxWidth: getCSSVariable('--devxgpt-floating-max-width', window.innerWidth * 0.9),
      maxHeight: getCSSVariable('--devxgpt-floating-max-height', window.innerHeight * 0.9),
    }),
    []
  );

  const { isDragging, handleMouseDown } = useDraggable({
    enabled: enableDrag,
    position,
    onPositionChange,
  });

  const { isResizing: isResizingLeft, handleResizeStart: handleResizeLeftStart } = useResizable({
    enabled: enableResize,
    size,
    position,
    onSizeChange,
    onPositionChange,
    direction: 'left',
    minWidth: constraints.minWidth,
    minHeight: constraints.minHeight,
    maxWidth: constraints.maxWidth,
    maxHeight: constraints.maxHeight,
  });

  const { isResizing: isResizingBottom, handleResizeStart: handleResizeBottomStart } =
    useResizable({
      enabled: enableResize,
      size,
      position,
      onSizeChange,
      onPositionChange,
      direction: 'bottom',
      minWidth: constraints.minWidth,
      minHeight: constraints.minHeight,
      maxWidth: constraints.maxWidth,
      maxHeight: constraints.maxHeight,
    });

  const isResizing = isResizingLeft || isResizingBottom;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className={`${styles.window} ${isDragging ? styles.dragging : ''} ${isResizing ? styles.resizing : ''}`}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Chat window"
    >
      <div
        className={navigationLayout === 'sidebar' ? styles.dragHandle : styles.dragHandleHorizontal}
        onMouseDown={handleMouseDown}
        aria-label="Drag to move"
      />
      {children}
      {enableResize && (
        <>
          <div
            className={styles.resizeHandleLeft}
            onMouseDown={handleResizeLeftStart}
            aria-label="Resize width"
          />
          <div
            className={styles.resizeHandleBottom}
            onMouseDown={handleResizeBottomStart}
            aria-label="Resize height"
          />
        </>
      )}
    </div>
  );
}
